import React, { useState } from 'react';
import { Table, Button, Popconfirm, Tag, Typography, Badge, Modal, List, message } from 'antd';
import { PlusOutlined, PaperClipOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  EXPENSE_TYPE_MAP,
  PAYMENT_NATURE_MAP,
  PAYMENT_NATURE_COLOR,
} from '../../constants/financeConstants';
import type { FinanceRecord, FinanceAttachment } from '../../types/finance';
import { getAttachmentDownloadUrl, EntityType } from '../../services/finance';
import FilePreview from '../../pages/Case/components/shared/FilePreview';

const { Text } = Typography;

interface FinanceRecordListProps {
  records: FinanceRecord[];
  leadId: number;
  canEdit: boolean;
  loading?: boolean;
  onAdd: () => void;
  onDelete: (recordId: number) => void;
  onRefresh?: () => void;
}

const FinanceRecordList: React.FC<FinanceRecordListProps> = ({
  records,
  leadId,
  canEdit,
  loading = false,
  onAdd,
  onDelete,
  onRefresh,
}) => {
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<FinanceAttachment[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewFile, setPreviewFile] = useState<any>(null);

  /**
   * Format amount with currency symbol and thousand separators
   */
  const formatAmount = (amount: number | string, isRefund: boolean): React.ReactNode => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    const formattedAmount = numAmount.toLocaleString('zh-CN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return (
      <Text style={{ color: isRefund ? '#cf1322' : '#000' }}>
        {isRefund ? '-' : ''}¥{formattedAmount}
      </Text>
    );
  };

  /**
   * Handle view attachments
   */
  const handleViewAttachments = (attachments: FinanceAttachment[]) => {
    setSelectedAttachments(attachments);
    setAttachmentModalVisible(true);
  };

  /**
   * Handle preview attachment
   */
  const handlePreviewAttachment = async (attachment: FinanceAttachment) => {
    try {
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(EntityType.LEAD, leadId, attachment.id);
      
      setPreviewFile({
        file_url: response.download_url,
        original_filename: attachment.file_name,
        content_type: attachment.file_type,
      });
      setPreviewVisible(true);
      setAttachmentModalVisible(false);
    } catch (error) {
      console.error('Failed to get attachment URL:', error);
      message.error('获取附件链接失败');
    }
  };

  /**
   * Handle download attachment
   */
  const handleDownloadAttachment = async (attachment: FinanceAttachment) => {
    try {
      // 获取临时下载URL
      const response = await getAttachmentDownloadUrl(EntityType.LEAD, leadId, attachment.id);
      
      // 在新窗口打开下载链接
      window.open(response.download_url, '_blank');
      message.success('开始下载');
    } catch (error) {
      console.error('Failed to download attachment:', error);
      message.error('下载失败');
    }
  };

  /**
   * Render attachment count badge
   */
  const renderAttachments = (attachments: FinanceAttachment[]): React.ReactNode => {
    if (!attachments || attachments.length === 0) {
      return <Text type="secondary">-</Text>;
    }

    return (
      <div 
        style={{ cursor: 'pointer', display: 'inline-block' }}
        onClick={(e) => {
          e.stopPropagation();
          handleViewAttachments(attachments);
        }}
      >
        <Badge count={attachments.length} showZero={false}>
          <PaperClipOutlined style={{ fontSize: 16 }} />
        </Badge>
      </div>
    );
  };

  /**
   * Table columns definition
   */
  const columns: ColumnsType<FinanceRecord> = [
    {
      title: '费用类型',
      dataIndex: 'expense_type',
      key: 'expense_type',
      width: 80,
      render: (type: string) => EXPENSE_TYPE_MAP[type] || type,
    },
    {
      title: '款项性质',
      dataIndex: 'payment_nature',
      key: 'payment_nature',
      width: 80,
      render: (nature: string) => (
        <Tag color={PAYMENT_NATURE_COLOR[nature]}>
          {PAYMENT_NATURE_MAP[nature] || nature}
        </Tag>
      ),
    },
    {
      title: '动款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (amount: number | string, record: FinanceRecord) => {
        const isRefund = record.payment_nature === 'REFUND';
        return formatAmount(amount, isRefund);
      },
    },
    {
      title: '动款时间',
      dataIndex: 'payment_time',
      key: 'payment_time',
      width: 150,
      render: (time: string) =>
        time ? dayjs(time).format('YYYY-MM-DD HH:mm') : <Text type="secondary">-</Text>,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 80,
      ellipsis: true,
      render: (remark: string) => remark || <Text type="secondary">-</Text>,
    },
    {
      title: '附件',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 60,
      align: 'center',
      render: (attachments: FinanceAttachment[]) => renderAttachments(attachments),
    },
    ...(canEdit ? [{
      title: '操作',
      key: 'action',
      width: 60,
      render: (_: any, record: FinanceRecord) => (
        <Popconfirm
          title="确认删除？"
          description="删除后记录将不再显示，但会保留在系统中以便审计"
          onConfirm={() => onDelete(record.id)}
          okText="确认"
          cancelText="取消"
        >
          <Button type="link" danger size="small">
            删除
          </Button>
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div className="finance-record-list">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        {canEdit && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAdd}
          >
            添加财务记录
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={records}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={{ x: canEdit ? 610 : 550 }}
        locale={{
          emptyText: '暂无数据',
        }}
      />

      {/* 附件列表弹窗 */}
      <Modal
        title="附件列表"
        open={attachmentModalVisible}
        onCancel={() => setAttachmentModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={selectedAttachments}
          renderItem={(attachment: FinanceAttachment) => (
            <List.Item
              actions={[
                <Button
                  key="preview"
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() => handlePreviewAttachment(attachment)}
                >
                  预览
                </Button>,
                <Button
                  key="download"
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadAttachment(attachment)}
                >
                  下载
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<PaperClipOutlined style={{ fontSize: 20 }} />}
                title={attachment.file_name}
                description={`大小: ${attachment.file_size ? (attachment.file_size / 1024).toFixed(2) + ' KB' : '未知'}`}
              />
            </List.Item>
          )}
        />
      </Modal>

      {/* 文件预览弹窗 */}
      {previewFile && (
        <FilePreview
          visible={previewVisible}
          fileUrl={previewFile.file_url}
          fileName={previewFile.original_filename}
          fileType={previewFile.content_type}
          onClose={() => {
            setPreviewVisible(false);
            setPreviewFile(null);
            setAttachmentModalVisible(true);
          }}
          onDownload={() => {
            const attachment = selectedAttachments.find(
              att => att.file_name === previewFile.original_filename
            );
            if (attachment) {
              handleDownloadAttachment(attachment);
            }
          }}
        />
      )}
    </div>
  );
};

export default FinanceRecordList;
